/**
 * Artifact Context Card
 * 
 * Displays detailed artifact information in the context panel (right sidebar).
 * Includes collapsible sections for details, children, states, transitions, and processes.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { EnhancedArtifact } from '../../../types/artifacts';
import { UnifiedArtifactDetails } from './UnifiedArtifactDetails';
import { UnifiedStatesList } from './UnifiedStatesList';
import { UnifiedTransitionsList } from './UnifiedTransitionsList';
import { ArtifactService } from '../../../services/api/artifactService';

interface ArtifactContextCardProps {
  artifact: EnhancedArtifact;
  isPrime?: boolean; // Whether this is a prime artifact
  onSectionToggle?: (section: string, isExpanded: boolean) => void;
  onArtifactUpdate?: (updated: EnhancedArtifact) => void;
  threadArtifactTreeId?: string; // Thread's artifact tree ID for fallback
  threadCompanyId?: string; // Thread's company ID for fallback
}

interface ContextSection {
  id: string;
  title: string;
  icon: string;
  defaultExpanded: boolean;
}

const CONTEXT_SECTIONS: ContextSection[] = [
  { id: 'details', title: 'Details', icon: '📋', defaultExpanded: true },
  { id: 'children', title: 'Children', icon: '👶', defaultExpanded: false },
  { id: 'states', title: 'States', icon: '⚡', defaultExpanded: false },
  { id: 'transitions', title: 'Transitions', icon: '🔄', defaultExpanded: false },
  { id: 'processes', title: 'Processes', icon: '⚙️', defaultExpanded: false },
];

export const ArtifactContextCard: React.FC<ArtifactContextCardProps> = ({
  artifact,
  isPrime,
  onSectionToggle,
  onArtifactUpdate,
  threadArtifactTreeId,
  threadCompanyId
}) => {
  // ISSUE-058/066 fix: Initialize expanded sections with useMemo to avoid recreation
  const defaultExpandedSections = useMemo(() => 
    new Set(CONTEXT_SECTIONS.filter(s => s.defaultExpanded).map(s => s.id)),
    []
  );
  
  const [expandedSections, setExpandedSections] = useState<Set<string>>(defaultExpandedSections);

  // ISSUE-058/066 fix: Memoize toggle handler
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const newExpanded = new Set(prev);
      const isExpanding = !newExpanded.has(sectionId);
      
      if (isExpanding) {
        newExpanded.add(sectionId);
      } else {
        newExpanded.delete(sectionId);
      }
      
      onSectionToggle?.(sectionId, isExpanding);
      return newExpanded;
    });
  }, [onSectionToggle]);

  // ISSUE-058/066 fix: Memoize isExpanded helper
  const isExpanded = useCallback((sectionId: string) => expandedSections.has(sectionId), [expandedSections]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      height: '100%',
      overflow: 'auto'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        backgroundColor: '#1e40af',
        borderRadius: '8px',
        borderBottom: '2px solid #3b82f6'
      }}>
        <h3 style={{
          margin: 0,
          color: 'white',
          fontSize: '16px',
          fontWeight: '600'
        }}>
          {artifact.name}
        </h3>
        <p style={{
          margin: '4px 0 0 0',
          color: '#93c5fd',
          fontSize: '12px'
        }}>
          {artifact.type}
        </p>
      </div>

      {/* Context Sections */}
      {CONTEXT_SECTIONS.map(section => (
        <ContextSectionCard
          key={section.id}
          section={section}
          artifact={artifact}
          isPrime={isPrime}
          isExpanded={isExpanded(section.id)}
          onToggle={() => toggleSection(section.id)}
          onArtifactUpdate={onArtifactUpdate}
          threadArtifactTreeId={threadArtifactTreeId}
          threadCompanyId={threadCompanyId}
        />
      ))}
    </div>
  );
};

interface ContextSectionCardProps {
  section: ContextSection;
  artifact: EnhancedArtifact;
  isPrime?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onArtifactUpdate?: (updated: EnhancedArtifact) => void;
  threadArtifactTreeId?: string;
  threadCompanyId?: string;
}

const ContextSectionCard: React.FC<ContextSectionCardProps> = ({
  section,
  artifact,
  isPrime,
  isExpanded,
  onToggle,
  onArtifactUpdate,
  threadArtifactTreeId,
  threadCompanyId
}) => {
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Section Header */}
      <div
        onClick={onToggle}
        style={{
          padding: '12px 16px',
          backgroundColor: isExpanded ? '#f0f9ff' : '#f9fafb',
          borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'background-color 0.2s ease',
          userSelect: 'none'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = isExpanded ? '#e0f2fe' : '#f3f4f6';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isExpanded ? '#f0f9ff' : '#f9fafb';
        }}
      >
        {isExpanded ? 
          <DownOutlined style={{ fontSize: '12px', color: '#3b82f6' }} /> : 
          <RightOutlined style={{ fontSize: '12px', color: '#6b7280' }} />
        }
        <span style={{ fontSize: '16px' }}>{section.icon}</span>
        <span style={{
          flex: 1,
          fontWeight: '500',
          fontSize: '14px',
          color: isExpanded ? '#1e40af' : '#374151'
        }}>
          {section.title}
        </span>
        <span style={{
          fontSize: '11px',
          color: '#9ca3af',
          backgroundColor: '#f3f4f6',
          padding: '2px 8px',
          borderRadius: '12px'
        }}>
          {getSectionCount(section.id, artifact)}
        </span>
      </div>

      {/* Section Content */}
      {isExpanded && (
        <div style={{
          padding: '12px',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          {renderSectionContent(section.id, artifact, isPrime, onArtifactUpdate, threadArtifactTreeId, threadCompanyId)}
        </div>
      )}
    </div>
  );
};

// Get count for section badge
const getSectionCount = (sectionId: string, artifact: EnhancedArtifact): string => {
  switch (sectionId) {
    case 'details':
      return '—';
    case 'children':
      return artifact.children?.length?.toString() || '0';
    case 'states':
      return artifact.states?.length?.toString() || '0';
    case 'transitions':
      return artifact.state_transitions?.length?.toString() || '0';
    case 'processes':
      return artifact.processes?.length?.toString() || '0';
    default:
      return '0';
  }
};

// Render section content - passed as prop to have access to context card props
const renderSectionContent = (
  sectionId: string, 
  artifact: EnhancedArtifact, 
  isPrime?: boolean,
  onArtifactUpdate?: (updated: EnhancedArtifact) => void,
  threadArtifactTreeId?: string,
  threadCompanyId?: string
): React.ReactNode => {
  switch (sectionId) {
    case 'details':
      return (
        <UnifiedArtifactDetails 
          artifact={artifact} 
          isPrime={isPrime}
          onUpdate={onArtifactUpdate}
        />
      );
    case 'children':
      return <ChildrenListContent artifact={artifact} threadArtifactTreeId={threadArtifactTreeId} threadCompanyId={threadCompanyId} />;
    case 'states':
      return (
        <UnifiedStatesList 
          artifact={artifact} 
          isPrime={isPrime}
        />
      );
    case 'transitions':
      return (
        <UnifiedTransitionsList 
          artifact={artifact} 
          isPrime={isPrime}
        />
      );
    case 'processes':
      return <ProcessesListContent artifact={artifact} />;
    default:
      return <div style={{ color: '#6b7280', fontSize: '12px' }}>Coming soon...</div>;
  }
};

// Note: Details section now uses UnifiedArtifactDetails component directly

// Children Section Content
const ChildrenListContent: React.FC<{ artifact: EnhancedArtifact; threadArtifactTreeId?: string; threadCompanyId?: string }> = ({ 
  artifact,
  threadArtifactTreeId,
  threadCompanyId 
}) => {
  const [children, setChildren] = useState<EnhancedArtifact[]>(artifact.children || []);
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);
  const [hasCheckedForChildren, setHasCheckedForChildren] = useState(false);
  const lastArtifactIdRef = useRef<string | null>(null);

  // Fetch children if not present in artifact data
  useEffect(() => {
    // If artifact ID changed, reset check flag
    if (lastArtifactIdRef.current !== artifact.id) {
      setHasCheckedForChildren(false);
      lastArtifactIdRef.current = artifact.id;
    }

    // If artifact already has children, use them
    if (artifact.children && artifact.children.length > 0) {
      setChildren(artifact.children);
      setHasCheckedForChildren(true);
      return;
    }

    // If we've already checked for this artifact ID, don't check again
    if (hasCheckedForChildren) {
      return;
    }

    // Fetch children from API
    const fetchChildren = async () => {
      setIsLoadingChildren(true);
      setHasCheckedForChildren(true);
      
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [ChildrenListContent] Fetching children for artifact:', {
            artifactId: artifact.id,
            artifactName: artifact.name,
            artifactTreeId: artifact.artifact_tree_id
          });
        }

        // Use thread's artifact_tree_id as fallback if artifact doesn't have it
        const effectiveArtifactTreeId = artifact.artifact_tree_id || threadArtifactTreeId;
        const effectiveCompanyId = artifact.company_id || threadCompanyId;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [ChildrenListContent] Effective IDs:', {
            artifactTreeId: { artifact: artifact.artifact_tree_id, thread: threadArtifactTreeId, effective: effectiveArtifactTreeId },
            companyId: { artifact: artifact.company_id, thread: threadCompanyId, effective: effectiveCompanyId }
          });
        }

        let childArtifacts: EnhancedArtifact[] = [];

        // Strategy 1: If artifact has tree_id (or thread has it), fetch hierarchy and find children in the tree
        if (effectiveArtifactTreeId) {
          try {
            if (process.env.NODE_ENV === 'development') {
              console.log('🌳 [ChildrenListContent] Using hierarchy endpoint for tree:', effectiveArtifactTreeId);
            }
            
            // Fetch hierarchy which returns nested structure
            const hierarchy = await ArtifactService.getHierarchy(effectiveCompanyId, effectiveArtifactTreeId);
            
            // Find this artifact in the hierarchy and extract its children
            const findArtifactAndChildren = (artifacts: EnhancedArtifact[]): EnhancedArtifact[] | null => {
              for (const a of artifacts) {
                if (a.id === artifact.id && a.children && a.children.length > 0) {
                  return a.children;
                }
                if (a.children && a.children.length > 0) {
                  const found = findArtifactAndChildren(a.children);
                  if (found) return found;
                }
              }
              return null;
            };
            
            const foundChildren = findArtifactAndChildren(hierarchy);
            if (foundChildren) {
              childArtifacts = foundChildren;
            } else {
              // Fallback: fetch all artifacts in tree and filter by parent_ref
              if (process.env.NODE_ENV === 'development') {
                console.log('🔄 [ChildrenListContent] Artifact not found in hierarchy, fetching all artifacts in tree');
              }
              const allArtifacts = await ArtifactService.getArtifacts(
                'company',
                effectiveArtifactTreeId,
                undefined,
                effectiveCompanyId
              );
              
              childArtifacts = allArtifacts.filter(a => {
                const parentId = (a as any).parent_ref || (a as any).parent_id;
                return parentId === artifact.id;
              });
            }
          } catch (hierarchyError) {
            console.warn('⚠️ [ChildrenListContent] Hierarchy fetch failed, trying alternative method:', hierarchyError);
            // Fall through to alternative method
          }
        }

        // Strategy 2: If no tree_id or hierarchy failed, try fetching artifact by ID (might include children)
        if (childArtifacts.length === 0) {
          try {
            if (process.env.NODE_ENV === 'development') {
              console.log('🔄 [ChildrenListContent] Fetching artifact by ID to get children');
            }
            
            const fullArtifact = await ArtifactService.getArtifactById(artifact.id);
            if (fullArtifact && fullArtifact.children && fullArtifact.children.length > 0) {
              childArtifacts = fullArtifact.children;
            }
          } catch (idError) {
            console.warn('⚠️ [ChildrenListContent] Fetch by ID failed:', idError);
          }
        }

        // Strategy 3: Last resort - fetch all artifacts (with tree_id if available) and filter
        if (childArtifacts.length === 0 && effectiveArtifactTreeId) {
          try {
            if (process.env.NODE_ENV === 'development') {
              console.log('🔄 [ChildrenListContent] Fetching all artifacts in tree as last resort');
            }
            
            const allArtifacts = await ArtifactService.getArtifacts(
              'company',
              effectiveArtifactTreeId,
              undefined,
              effectiveCompanyId
            );
            
            childArtifacts = allArtifacts.filter(a => {
              const parentId = (a as any).parent_ref || (a as any).parent_id;
              return parentId === artifact.id;
            });
          } catch (error) {
            console.error('❌ [ChildrenListContent] Failed to fetch artifacts:', error);
          }
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [ChildrenListContent] Found children:', {
            artifactId: artifact.id,
            childrenCount: childArtifacts.length,
            children: childArtifacts.map(c => ({ id: c.id, name: c.name }))
          });
        }

        setChildren(childArtifacts);
      } catch (error) {
        console.error('❌ [ChildrenListContent] Failed to fetch children:', error);
        // Keep existing children (if any) or empty array
      } finally {
        setIsLoadingChildren(false);
      }
    };

    fetchChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifact.id, threadArtifactTreeId, threadCompanyId]); // Re-fetch if artifact or thread context changes

  if (isLoadingChildren) {
    return (
      <div style={{ color: '#6b7280', fontSize: '12px', fontStyle: 'italic' }}>
        Loading children...
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div style={{ color: '#6b7280', fontSize: '12px', fontStyle: 'italic' }}>
        No children artifacts
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {children.map(child => (
        <div
          key={child.id}
          style={{
            padding: '8px 10px',
            backgroundColor: '#f9fafb',
            borderRadius: '4px',
            border: '1px solid #e5e7eb',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f9ff';
            e.currentTarget.style.borderColor = '#3b82f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
            e.currentTarget.style.borderColor = '#e5e7eb';
          }}
        >
          <div style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: '#3b82f6'
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '500', color: '#374151' }}>{child.name}</div>
            <div style={{ fontSize: '10px', color: '#9ca3af' }}>{child.type}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Note: States section now uses UnifiedStatesList component directly

// Note: Transitions section now uses UnifiedTransitionsList component directly

// Processes Section Content
const ProcessesListContent: React.FC<{ artifact: EnhancedArtifact }> = ({ artifact }) => {
  const processes = artifact.processes || [];
  
  if (processes.length === 0) {
    return (
      <div style={{ color: '#6b7280', fontSize: '12px', fontStyle: 'italic' }}>
        No processes defined
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {processes.map(process => (
        <div
          key={process.id}
          style={{
            padding: '8px 10px',
            backgroundColor: '#f9fafb',
            borderRadius: '4px',
            border: '1px solid #e5e7eb',
            fontSize: '12px'
          }}
        >
          <div style={{ fontWeight: '500', color: '#374151', marginBottom: '2px' }}>
            {process.name}
          </div>
          <div style={{ fontSize: '10px', color: '#9ca3af' }}>
            {process.process_type}
          </div>
        </div>
      ))}
    </div>
  );
};

